// src/AttendanceReport.jsx - Corrected 24-Hour Time Calculation
import React, { useState, useEffect } from 'react';
import { ref, onValue, get } from 'firebase/database';
import { database } from './firebase';
import { useNavigate } from 'react-router-dom';
import BossNavigation from "./BossNavigation";
import Navigation from "./Navigation";

export default function AttendanceReport() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [monthlyData, setMonthlyData] = useState([]);
  const [summary, setSummary] = useState({
    totalDays: 0,
    presentDays: 0,
    halfDays: 0,
    absentDays: 0,
    notMarkedDays: 0,
    totalHours: 0,
    totalMinutes: 0,
    totalMoneyTaken: 0,
    attendancePercentage: 0,
    totalHoursDisplay: '0h'
  });
  const [loading, setLoading] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const d = new Date();
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().split("T")[0];
  };

  // Check if date is past (before today)
  const isPastDate = (dateStr) => {
    const today = getTodayDate();
    return dateStr < today;
  };

  // Check if date is future (after today)
  const isFutureDate = (dateStr) => {
    const today = getTodayDate();
    return dateStr > today;
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  useEffect(() => {
    if (selectedEmployee) {
      loadMonthlyAttendance();
    }
  }, [selectedEmployee, selectedYear, selectedMonth]);

  const loadEmployees = () => {
    const employeesRef = ref(database, 'employees');
    onValue(employeesRef, (snapshot) => {
      if (snapshot.exists()) {
        const employeesData = [];
        snapshot.forEach((child) => {
          employeesData.push({
            id: child.key,
            ...child.val()
          });
        });
        setEmployees(employeesData);
      }
    });
  };

  // Parse time to 24-hour format minutes - FULLY CORRECTED for all formats
  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr || timeStr === '-' || timeStr === null) return 0;
    try {
      let hours = 0, minutes = 0;
      let timeStrClean = timeStr.trim().toLowerCase();
      
      // Check for AM/PM
      const isPM = timeStrClean.includes('pm');
      const isAM = timeStrClean.includes('am');
      
      // Remove AM/PM and extract time part
      let timePart = timeStrClean;
      if (isPM || isAM) {
        timePart = timeStrClean.replace(/am|pm/g, '').trim();
      }
      
      // Split time parts (support HH:MM:SS or HH:MM)
      const parts = timePart.split(':');
      if (parts.length >= 2) {
        hours = parseInt(parts[0], 10);
        minutes = parseInt(parts[1], 10);
      }
      
      // Validate
      if (isNaN(hours)) hours = 0;
      if (isNaN(minutes)) minutes = 0;
      
      // Convert to 24-hour format
      if (isPM && hours < 12) {
        hours += 12;
      }
      if (isAM && hours === 12) {
        hours = 0;
      }
      
      // Ensure hours are 0-23
      hours = Math.min(23, Math.max(0, hours));
      minutes = Math.min(59, Math.max(0, minutes));
      
      return (hours * 60) + minutes;
    } catch (error) {
      console.error('Time parse error:', timeStr, error);
      return 0;
    }
  };

  // Format minutes to hours and minutes display
  const formatHoursMinutes = (totalMinutes) => {
    if (totalMinutes <= 0) return '-';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  // Format to HH:MM for display
  const formatToHHMM = (totalMinutes) => {
    if (totalMinutes <= 0) return '-';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Helper to get check-in time from record
  const getCheckInTime = (record) => {
    return record?.checkInTime || record?.checkIn || null;
  };

  // Helper to get check-out time from record
  const getCheckOutTime = (record) => {
    return record?.checkOutTime || record?.checkOut || null;
  };

  // Calculate worked minutes from check-in and check-out
  const calculateWorkedMinutes = (checkIn, checkOut) => {
    if (!checkIn || !checkOut || checkOut === '-' || checkIn === '-') return 0;
    
    const checkInMinutes = parseTimeToMinutes(checkIn);
    const checkOutMinutes = parseTimeToMinutes(checkOut);
    
    if (checkInMinutes === 0 || checkOutMinutes === 0) return 0;
    
    let workedMinutes = checkOutMinutes - checkInMinutes;
    // Handle overnight shifts
    if (workedMinutes < 0) {
      workedMinutes += 24 * 60;
    }
    // Cap at 24 hours
    if (workedMinutes > 24 * 60) {
      workedMinutes = 24 * 60;
    }
    
    return workedMinutes;
  };

  const getAttendanceStatus = (record, dateStr) => {
    if (isFutureDate(dateStr)) return 'not-marked';
    if (isPastDate(dateStr) && !record) return 'absent';
    if (!record) return 'not-marked';
    if (record.halfDay === true) return 'half-day';
    
    const checkIn = getCheckInTime(record);
    const checkOut = getCheckOutTime(record);
    
    if (checkIn && checkOut && checkOut !== '-') return 'present';
    if (checkIn && (!checkOut || checkOut === '-')) return 'at-work';
    if (record.status === 'present') return 'present';
    if (record.status === 'half-day') return 'half-day';
    if (record.status === 'absent') return 'absent';
    return 'present';
  };

  const getFullStatusText = (status) => {
    switch(status) {
      case 'present': return 'PRESENT';
      case 'half-day': return 'HALF DAY';
      case 'absent': return 'ABSENT';
      case 'at-work': return 'AT WORK';
      default: return 'NOT MARKED';
    }
  };

  const loadMonthlyAttendance = async () => {
    if (!selectedEmployee) return;
    
    setLoading(true);
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const monthlyReport = [];
    
    let totalPresent = 0;
    let totalHalfDays = 0;
    let totalNotMarked = 0;
    let totalAbsent = 0;
    let totalMinutesWorked = 0;
    let totalMoney = 0;

    for (let day = 1; day <= daysInMonth; day++) {
      const date = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const attendanceRef = ref(database, `attendance/${date}/${selectedEmployee.id}`);
      
      try {
        const snapshot = await get(attendanceRef);
        const record = snapshot.val();
        const status = getAttendanceStatus(record, date);
        
        let workedMinutes = 0;
        const checkIn = getCheckInTime(record);
        const checkOut = getCheckOutTime(record);
        
        // Calculate worked minutes using the helper function
        if (status === 'present' || status === 'half-day') {
          if (record?.halfDay) {
            workedMinutes = 240; // 4 hours for half day
          } else if (checkIn && checkOut && checkOut !== '-') {
            workedMinutes = calculateWorkedMinutes(checkIn, checkOut);
          }
        }
        
        // If record has stored minutes and it's reasonable, prefer that
        if (record?.totalMinutes && record?.totalMinutes > 0 && record.totalMinutes <= 1440 && workedMinutes === 0) {
          workedMinutes = record.totalMinutes;
        }
        
        switch(status) {
          case 'present':
            totalPresent++;
            totalMinutesWorked += workedMinutes;
            break;
          case 'half-day':
            totalHalfDays++;
            totalPresent += 0.5;
            totalMinutesWorked += workedMinutes;
            break;
          case 'at-work':
            totalPresent++;
            break;
          case 'absent':
            totalAbsent++;
            break;
          case 'not-marked':
            totalNotMarked++;
            break;
        }
        
        const dayData = {
          date: date,
          day: day,
          dayName: new Date(date).toLocaleDateString('en-US', { weekday: 'short' }),
          status: status,
          fullStatusText: getFullStatusText(status),
          checkInTime: checkIn || '-',
          checkOutTime: checkOut || '-',
          workedMinutes: workedMinutes,
          totalHoursDisplay: workedMinutes > 0 ? formatHoursMinutes(workedMinutes) : '-',
          totalHoursHHMM: workedMinutes > 0 ? formatToHHMM(workedMinutes) : '-',
          moneyTaken: record?.moneyTaken || 0,
          remark: record?.remark || '',
          verifiedBy: record?.verifiedBy || null,
          signatureUrl: selectedEmployee?.signatureUrl || null
        };
        
        monthlyReport.push(dayData);
        
        if (record?.moneyTaken) {
          totalMoney += record.moneyTaken || 0;
        }
      } catch (error) {
        console.error('Error loading attendance for date:', date, error);
        const isPast = isPastDate(date);
        const isFuture = isFutureDate(date);
        const status = isPast ? 'absent' : 'not-marked';
        
        monthlyReport.push({
          date: date,
          day: day,
          dayName: new Date(date).toLocaleDateString('en-US', { weekday: 'short' }),
          status: status,
          fullStatusText: isPast ? 'ABSENT' : (isFuture ? 'NOT MARKED' : 'NOT MARKED'),
          checkInTime: '-',
          checkOutTime: '-',
          workedMinutes: 0,
          totalHoursDisplay: '-',
          totalHoursHHMM: '-',
          moneyTaken: 0,
          remark: '',
          verifiedBy: null,
          signatureUrl: null
        });
        
        if (isPast) {
          totalAbsent++;
        } else {
          totalNotMarked++;
        }
      }
    }
    
    const totalDays = daysInMonth;
    const presentDays = Math.floor(totalPresent);
    const halfDays = totalHalfDays;
    const absentDays = totalAbsent;
    const notMarkedDays = totalNotMarked;
    const attendancePercentage = totalDays > 0 ? ((totalPresent / totalDays) * 100).toFixed(1) : 0;
    
    setMonthlyData(monthlyReport);
    setSummary({
      totalDays: totalDays,
      presentDays: presentDays,
      halfDays: halfDays,
      absentDays: absentDays,
      notMarkedDays: notMarkedDays,
      totalHours: (totalMinutesWorked / 60).toFixed(2),
      totalMinutes: totalMinutesWorked,
      totalHoursDisplay: formatHoursMinutes(totalMinutesWorked),
      totalMoneyTaken: totalMoney,
      attendancePercentage: attendancePercentage
    });
    
    setLoading(false);
  };

  const printReport = () => {
    const originalTitle = document.title;
    document.title = `Attendance_${selectedEmployee?.name}_${getMonthName(selectedMonth)}_${selectedYear}`;
    
    const employeeSignatureUrl = selectedEmployee?.signatureUrl || '';
    const hasFaceVerification = monthlyData.some(day => day.verifiedBy === 'face-verified');
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Attendance Report - ${selectedEmployee?.name}</title>
            <meta charset="UTF-8">
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              @page { size: A4; margin: 8mm; }
              body {
                font-family: 'Segoe UI', Arial, sans-serif;
                font-size: 9pt;
                line-height: 1.2;
                color: #000;
                background: white;
              }
              .report-container { max-width: 100%; }
              .header {
                text-align: center;
                margin-bottom: 10px;
                padding-bottom: 8px;
                border-bottom: 2px solid #000;
              }
              .header h1 { font-size: 14pt; margin-bottom: 5px; letter-spacing: 2px; }
              .header p { font-size: 9pt; margin: 2px 0; }
              .summary-grid {
                display: grid;
                grid-template-columns: repeat(7, 1fr);
                gap: 5px;
                margin-bottom: 12px;
              }
              .summary-card {
                border: 1px solid #000;
                padding: 5px;
                text-align: center;
              }
              .summary-number { font-size: 12pt; font-weight: bold; }
              .summary-label { font-size: 7pt; margin-top: 2px; }
              table {
                width: 100%;
                border-collapse: collapse;
                margin: 8px 0 15px 0;
              }
              th, td {
                border: 1px solid #000;
                padding: 5px 3px;
                text-align: center;
                font-size: 7pt;
              }
              th { background-color: #000; color: white; font-weight: bold; }
              .present-row { background-color: #d4edda; }
              .half-day-row { background-color: #fff3cd; }
              .absent-row { background-color: #f8d7da; }
              .at-work-row { background-color: #cfe2ff; }
              .not-marked-row { background-color: #e9ecef; }
              .signature-img { max-width: 50px; max-height: 25px; object-fit: contain; }
              .signature-section {
                margin-top: 15px;
                margin-bottom: 10px;
                display: flex;
                justify-content: flex-end;
                clear: both;
              }
              .signature-box { text-align: center; width: 200px; }
              .signature-line {
                border-top: 1px solid #000;
                padding-top: 6px;
                margin-top: 6px;
              }
              .verification-text { font-size: 6pt; color: #4caf50; margin-top: 4px; }
              .note-text {
                font-size: 6pt;
                color: #666;
                text-align: center;
                margin-top: 10px;
                padding-top: 6px;
                border-top: 1px solid #ddd;
              }
              @media print {
                body { margin: 0; padding: 0; }
                button { display: none; }
              }
            </style>
          </head>
          <body>
            <div class="report-container">
              <div class="header">
                <h1>ATTENDANCE REPORT</h1>
                <p><strong>${selectedEmployee?.name || ''}</strong> | ${selectedEmployee?.role || 'Staff'} | ID: ${selectedEmployee?.id?.slice(-6) || 'N/A'}</p>
                <p>${getMonthName(selectedMonth)} ${selectedYear}</p>
              </div>
              
              <div class="summary-grid">
                <div class="summary-card"><div class="summary-number">${summary.totalDays}</div><div class="summary-label">TOTAL DAYS</div></div>
                <div class="summary-card"><div class="summary-number">${summary.presentDays}</div><div class="summary-label">PRESENT</div></div>
                <div class="summary-card"><div class="summary-number">${summary.halfDays}</div><div class="summary-label">HALF DAYS</div></div>
                <div class="summary-card"><div class="summary-number">${summary.absentDays}</div><div class="summary-label">ABSENT</div></div>
                <div class="summary-card"><div class="summary-number">${summary.attendancePercentage}%</div><div class="summary-label">ATTENDANCE RATE</div></div>
                <div class="summary-card"><div class="summary-number">${summary.totalHoursDisplay}</div><div class="summary-label">TOTAL HOURS</div></div>
                <div class="summary-card"><div class="summary-number">₹${summary.totalMoneyTaken}</div><div class="summary-label">TOTAL MONEY</div></div>
              </div>
              
              <tr>
                <thead>
                  <tr>
                    <th>DATE</th><th>DAY</th><th>STATUS</th><th>IN</th><th>OUT</th><th>DURATION</th><th>MONEY</th><th>REMARK</th><th>SIGNATURE</th>
                  </tr>
                </thead>
                <tbody>
                  ${monthlyData.map(day => {
                    let rowClass = '';
                    let fullStatus = '';
                    let statusColor = '';
                    
                    if (day.status === 'present') {
                      rowClass = 'present-row';
                      fullStatus = 'PRESENT';
                      statusColor = '#155724';
                    } else if (day.status === 'half-day') {
                      rowClass = 'half-day-row';
                      fullStatus = 'HALF DAY';
                      statusColor = '#856404';
                    } else if (day.status === 'absent') {
                      rowClass = 'absent-row';
                      fullStatus = 'ABSENT';
                      statusColor = '#721c24';
                    } else if (day.status === 'at-work') {
                      rowClass = 'at-work-row';
                      fullStatus = 'AT WORK';
                      statusColor = '#084298';
                    } else {
                      rowClass = 'not-marked-row';
                      fullStatus = 'NOT MARKED';
                      statusColor = '#6c757d';
                    }
                    
                    const showSignature = (day.moneyTaken > 0 && employeeSignatureUrl);
                    const checkInShort = day.checkInTime !== '-' ? (day.checkInTime.length > 5 ? day.checkInTime.slice(0,5) : day.checkInTime) : '-';
                    const checkOutShort = day.checkOutTime !== '-' ? (day.checkOutTime.length > 5 ? day.checkOutTime.slice(0,5) : day.checkOutTime) : '-';
                    
                    return `
                      <tr class="${rowClass}">
                        <td>${day.date.slice(5)}</td>
                        <td>${day.dayName}</td>
                        <td style="color: ${statusColor}; font-weight: bold;">${fullStatus}</td>
                        <td>${checkInShort}</td>
                        <td>${checkOutShort}</td>
                        <td>${day.totalHoursDisplay}</td>
                        <td>${day.moneyTaken > 0 ? '₹' + day.moneyTaken : '-'}</td>
                        <td style="text-align: left;">${day.remark ? (day.remark.length > 20 ? day.remark.slice(0, 20) + '...' : day.remark) : '-'}</td>
                        <td style="text-align: center;">
                          ${showSignature ? `<img src="${employeeSignatureUrl}" class="signature-img" />` : '-'}
                        </td>
                      </tr>
                    `;
                  }).join('')}
                </tbody>
                <tfoot>
                  <tr style="background-color: #f0f0f0; font-weight: bold;">
                    <td colspan="9" style="padding: 6px; text-align: center;">
                      SUMMARY: TOTAL ${summary.totalDays} DAYS | PRESENT ${summary.presentDays} | HALF DAYS ${summary.halfDays} | 
                      ABSENT ${summary.absentDays} | NOT MARKED ${summary.notMarkedDays} | 
                      RATE ${summary.attendancePercentage}% | TOTAL ${summary.totalHoursDisplay} | TOTAL MONEY ₹${summary.totalMoneyTaken}
                    </td>
                  </tr>
                </tfoot>
              20
              
              <div class="signature-section">
                <div class="signature-box">
                  ${employeeSignatureUrl ? 
                    `<img src="${employeeSignatureUrl}" style="max-width: 120px; max-height: 50px; border: 1px solid #ccc; padding: 5px;" /><br/>` : 
                    '<div style="color: #999;">No signature on file</div>'
                  }
                  <div class="signature-line">EMPLOYEE SIGNATURE</div>
                  ${hasFaceVerification ? 
                    '<div class="verification-text">✓ Face Verified for money transactions</div>' : 
                    '<div class="verification-text" style="color:#999;">No face verification done</div>'
                  }
                </div>
              </div>
              
              <div class="note-text">
                Note: Signature appears only on days when money was taken | Generated: ${new Date().toLocaleString()}
              </div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 1000);
    }
    
    document.title = originalTitle;
  };

  const getMonthName = (month) => {
    const months = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
    return months[month];
  };

  const getShortMonthName = (month) => {
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    return months[month];
  };

  const getYearRange = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 2; i <= currentYear + 1; i++) {
      years.push(i);
    }
    return years;
  };

  return (
    <div style={styles.container}>
      <BossNavigation />
      <Navigation role="hr" />
      
      <div style={styles.headerBar}>
        <h1 style={styles.mainTitle}>📊 ATTENDANCE REPORT</h1>
        <button onClick={() => navigate('/manual-entry')} style={styles.manualEntryBtn}>
          ✍️ MANUAL
        </button>
      </div>

      <button style={styles.mobileFilterBtn} onClick={() => setShowMobileMenu(!showMobileMenu)}>
        {showMobileMenu ? '▲ HIDE FILTERS' : '▼ SHOW FILTERS'}
      </button>

      <div style={{ ...styles.controls, display: showMobileMenu ? 'block' : 'none' }}>
        <div style={styles.selectionSection}>
          <div style={styles.formGroup}>
            <label style={styles.label}>EMPLOYEE:</label>
            <select 
              value={selectedEmployee?.id || ''} 
              onChange={(e) => {
                const employeeId = e.target.value;
                const emp = employees.find(emp => emp.id === employeeId);
                setSelectedEmployee(emp || null);
                setShowMobileMenu(false);
              }}
              style={styles.select}
            >
              <option value="">-- SELECT EMPLOYEE --</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.name} - {emp.role || 'Staff'}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>YEAR:</label>
            <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} style={styles.select}>
              {getYearRange().map(year => <option key={year} value={year}>{year}</option>)}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>MONTH:</label>
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))} style={styles.select}>
              {Array(12).fill().map((_, i) => <option key={i} value={i}>{getShortMonthName(i)}</option>)}
            </select>
          </div>

          {selectedEmployee && (
            <button onClick={printReport} style={styles.printBtn}>
              🖨️ PRINT
            </button>
          )}
        </div>
      </div>

      {selectedEmployee && !loading && (
        <div style={styles.reportContent}>
          <div style={styles.reportHeader}>
            <h2 style={styles.reportTitle}>ATTENDANCE REPORT</h2>
            <p style={styles.employeeInfo}>
              <strong>{selectedEmployee.name}</strong> | {selectedEmployee.role || 'Staff'} | ID: {selectedEmployee.id?.slice(-6) || 'N/A'}
            </p>
            <p style={styles.monthInfo}>{getMonthName(selectedMonth)} {selectedYear}</p>
          </div>

          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}><div style={styles.summaryNumber}>{summary.totalDays}</div><div style={styles.summaryLabel}>TOTAL</div></div>
            <div style={styles.summaryCard}><div style={styles.summaryNumber}>{summary.presentDays}</div><div style={styles.summaryLabel}>PRESENT</div></div>
            <div style={styles.summaryCard}><div style={styles.summaryNumber}>{summary.halfDays}</div><div style={styles.summaryLabel}>HALF</div></div>
            <div style={styles.summaryCard}><div style={styles.summaryNumber}>{summary.absentDays}</div><div style={styles.summaryLabel}>ABSENT</div></div>
            <div style={styles.summaryCard}><div style={styles.summaryNumber}>{summary.notMarkedDays}</div><div style={styles.summaryLabel}>NOT MARKED</div></div>
            <div style={styles.summaryCard}><div style={styles.summaryNumber}>{summary.attendancePercentage}%</div><div style={styles.summaryLabel}>RATE</div></div>
            <div style={styles.summaryCard}><div style={styles.summaryNumber}>{summary.totalHoursDisplay}</div><div style={styles.summaryLabel}>HOURS</div></div>
            <div style={styles.summaryCard}><div style={styles.summaryNumber}>₹{summary.totalMoneyTaken}</div><div style={styles.summaryLabel}>MONEY</div></div>
          </div>

          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={styles.th}>DATE</th><th style={styles.th}>DAY</th><th style={styles.th}>STATUS</th>
                  <th style={styles.th}>IN</th><th style={styles.th}>OUT</th><th style={styles.th}>DURATION</th>
                  <th style={styles.th}>MONEY</th><th style={styles.th}>REMARK</th><th style={styles.th}>SIGNATURE</th>
                </tr>
              </thead>
              <tbody>
                {monthlyData.map((day, index) => (
                  <tr key={index} style={{ 
                    backgroundColor: day.status === 'present' ? '#d4edda' : 
                                   day.status === 'half-day' ? '#fff3cd' :
                                   day.status === 'absent' ? '#f8d7da' :
                                   day.status === 'at-work' ? '#cfe2ff' : '#e9ecef'
                  }}>
                    <td style={styles.td}>{day.date.slice(5)}</td>
                    <td style={styles.td}>{day.dayName}</td>
                    <td style={styles.td}>
                      <span style={{ fontWeight: 'bold', color: day.status === 'present' ? '#155724' : 
                                     day.status === 'half-day' ? '#856404' : day.status === 'absent' ? '#721c24' :
                                     day.status === 'at-work' ? '#084298' : '#6c757d' }}>
                        {day.fullStatusText}
                      </span>
                    </td>
                    <td style={styles.td}>{day.checkInTime !== '-' ? (day.checkInTime.length > 5 ? day.checkInTime.slice(0,5) : day.checkInTime) : '-'}</td>
                    <td style={styles.td}>{day.checkOutTime !== '-' ? (day.checkOutTime.length > 5 ? day.checkOutTime.slice(0,5) : day.checkOutTime) : '-'}</td>
                    <td style={styles.td}>{day.totalHoursDisplay}</td>
                    <td style={styles.td}>{day.moneyTaken > 0 ? '₹' + day.moneyTaken : '-'}</td>
                    <td style={styles.td}>{day.remark ? (day.remark.length > 15 ? day.remark.slice(0, 15) + '...' : day.remark) : '-'}</td>
                    <td style={styles.td}>
                      {day.moneyTaken > 0 && selectedEmployee?.signatureUrl ? (
                        <img src={selectedEmployee.signatureUrl} alt="Signature" style={styles.tableSignature} />
                      ) : <span style={{ color: '#ccc' }}>-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={styles.tableFooter}>
                  <td colSpan="9" style={styles.footerText}>
                    SUMMARY: TOTAL ${summary.totalDays} DAYS | PRESENT ${summary.presentDays} | HALF ${summary.halfDays} | 
                    ABSENT ${summary.absentDays} | NOT MARKED ${summary.notMarkedDays} | RATE ${summary.attendancePercentage}% | 
                    TOTAL ${summary.totalHoursDisplay} | TOTAL MONEY ₹${summary.totalMoneyTaken}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div style={styles.signatureFooter}>
            <div style={styles.signatureFooterRight}>
              {selectedEmployee?.signatureUrl && (
                <img src={selectedEmployee.signatureUrl} alt="Signature" style={styles.footerSignature} />
              )}
              <div style={styles.signatureLine}>EMPLOYEE SIGNATURE</div>
              {monthlyData.some(day => day.verifiedBy === 'face-verified') && (
                <div style={styles.verificationIndicator}>✓ Face Verified</div>
              )}
            </div>
          </div>

          <div style={styles.reportFooter}>
            <p>Signature appears only when money is taken | Past dates auto-marked ABSENT | Future dates show NOT MARKED</p>
          </div>
        </div>
      )}

      {selectedEmployee && loading && (
        <div style={styles.loading}>
          <div style={styles.spinner}></div>
          <p>LOADING...</p>
        </div>
      )}

      {!selectedEmployee && (
        <div style={styles.noSelection}>
          <p>👈 SELECT AN EMPLOYEE TO VIEW REPORT</p>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { 
    maxWidth: '100%', 
    margin: '0', 
    padding: '10px', 
    backgroundColor: '#f5f5f5', 
    minHeight: '100vh' 
  },
  headerBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '15px',
    padding: '10px 15px',
    backgroundColor: '#1a1a2e',
    borderRadius: '10px',
    color: 'white'
  },
  mainTitle: { fontSize: '16px', margin: 0, letterSpacing: '1px' },
  manualEntryBtn: {
    padding: '8px 16px',
    backgroundColor: '#e67e22',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  mobileFilterBtn: {
    width: '100%',
    padding: '10px',
    backgroundColor: '#2c3e50',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    marginBottom: '10px',
    fontSize: '12px',
    fontWeight: 'bold',
    cursor: 'pointer'
  },
  controls: { 
    backgroundColor: 'white', 
    padding: '12px', 
    borderRadius: '10px', 
    marginBottom: '15px', 
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)' 
  },
  selectionSection: { 
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  formGroup: { 
    display: 'flex', 
    flexDirection: 'column', 
    gap: '4px' 
  },
  label: { fontSize: '11px', fontWeight: 'bold', color: '#555' },
  select: { 
    padding: '8px', 
    border: '1px solid #ddd', 
    borderRadius: '6px', 
    fontSize: '12px',
    width: '100%'
  },
  printBtn: { 
    padding: '10px', 
    backgroundColor: '#27ae60', 
    color: 'white', 
    border: 'none', 
    borderRadius: '8px', 
    cursor: 'pointer', 
    fontSize: '13px', 
    fontWeight: 'bold'
  },
  reportContent: { 
    backgroundColor: 'white', 
    padding: '12px', 
    borderRadius: '10px', 
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
    overflowX: 'auto'
  },
  reportHeader: { 
    textAlign: 'center', 
    marginBottom: '12px', 
    borderBottom: '2px solid #333', 
    paddingBottom: '8px' 
  },
  reportTitle: { fontSize: '14px', color: '#333', margin: 0, letterSpacing: '1px' },
  employeeInfo: { fontSize: '11px', color: '#666', margin: '5px 0' },
  monthInfo: { fontSize: '12px', color: '#27ae60', fontWeight: 'bold', margin: 0 },
  summaryGrid: { 
    display: 'grid', 
    gridTemplateColumns: 'repeat(4, 1fr)', 
    gap: '8px', 
    marginBottom: '15px' 
  },
  summaryCard: { 
    backgroundColor: '#f8f9fa', 
    padding: '8px', 
    borderRadius: '6px', 
    textAlign: 'center', 
    border: '1px solid #ddd' 
  },
  summaryNumber: { fontSize: '16px', fontWeight: 'bold', color: '#27ae60' },
  summaryLabel: { fontSize: '8px', color: '#666', letterSpacing: '0.5px' },
  loading: { textAlign: 'center', padding: '30px' },
  spinner: { 
    width: '30px', 
    height: '30px', 
    border: '3px solid #f3f3f3', 
    borderTop: '3px solid #27ae60', 
    borderRadius: '50%', 
    animation: 'spin 1s linear infinite', 
    margin: '0 auto 15px' 
  },
  noSelection: { textAlign: 'center', padding: '40px', backgroundColor: 'white', borderRadius: '10px', fontSize: '12px', color: '#999' },
  tableWrapper: { overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: '10px', minWidth: '700px', fontSize: '10px' },
  th: { 
    padding: '8px 4px', 
    textAlign: 'center', 
    border: '1px solid #ddd', 
    fontSize: '9px', 
    fontWeight: 'bold',
    backgroundColor: '#1a1a2e',
    color: 'white'
  },
  td: { padding: '6px 3px', border: '1px solid #ddd', fontSize: '9px', textAlign: 'center' },
  tableFooter: { backgroundColor: '#f0f0f0', fontWeight: 'bold' },
  footerText: { padding: '6px', fontSize: '8px', textAlign: 'center' },
  tableSignature: { maxWidth: '35px', maxHeight: '25px', border: '1px solid #ddd', borderRadius: '3px' },
  signatureFooter: { marginTop: '15px', display: 'flex', justifyContent: 'flex-end', padding: '8px', borderTop: '1px solid #ddd' },
  signatureFooterRight: { textAlign: 'center', width: '150px' },
  signatureLine: { borderTop: '1px solid #333', paddingTop: '6px', marginTop: '6px', fontSize: '8px', fontWeight: 'bold' },
  verificationIndicator: { fontSize: '7px', color: '#4caf50', marginTop: '4px' },
  footerSignature: { maxWidth: '80px', maxHeight: '35px', marginBottom: '5px', border: '1px solid #ddd', padding: '2px' },
  reportFooter: { marginTop: '10px', textAlign: 'center', fontSize: '7px', color: '#999', borderTop: '1px solid #ddd', paddingTop: '8px' }
};

// Add spinner animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
  @media print { .no-print { display: none !important; } }
`;
document.head.appendChild(styleSheet);